import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Play, Eye, Trophy, Trash2, RotateCcw, UserX, SkipForward, ChevronUp, ChevronDown, GripVertical, LifeBuoy } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import BattleTimerPanel from "@/components/timer/BattleTimerPanel";
import CircleTimerPanel from "@/components/timer/CircleTimerPanel";
import DryRunPanel from "@/components/director/DryRunPanel";
import ManagerMessageButton from "@/components/comms/ManagerMessageButton";

import { BACKEND_URL } from '../../utils/apiBase';
const API = `${BACKEND_URL}/api`;

const JudgeStatusDot = ({ status }) => {
  const color = status === "active" ? "bg-green-500" : "bg-orange-500";
  const title = status === "active" ? "Connecté" : "Non connecté";
  return <span className={`inline-block w-3 h-3 rounded-full ${color} mr-2`} title={title} data-testid={`judge-status-dot-${status}`} />;
};

export const ControlTab = ({
  currentBattle, currentCircle, judges, athletes, tournaments, judgesCount,
  revealVote, revealAllVotes, finishBattle, finishBattleAndNext, getNextBattle,
  resetBattle, setForfeitBattleId, setForfeitModalOpen,
  circleRevealVote, circleRevealAll, circleFinish, finishCircleAndNext,
  circleReset, circleDeleteVote, circleForfeit, circleCancelForfeit,
  manualBattleScores, setManualBattleScores, submitManagerBattleVote,
  manualCircleScores, setManualCircleScores, submitManagerCircleVote,
  upcomingSections, reorderSections,
  startBattle, loadCurrentBattle, loadUpcomingEvents, startCircle,
  countdownMode, battleTimers, circleTimers, refreshBattleTimer, refreshCircleTimer,
  simulationMode, eventId: eventIdProp,
  commsSendMessage, commsHelpStates, commsResolveHelp,
}) => {
  const { t } = useI18n();
  const [judgeStatuses, setJudgeStatuses] = useState({});
  const [dragIdx, setDragIdx] = useState(null);

  const eventId = eventIdProp || currentBattle?.battle?.event_id || currentCircle?.circle?.event_id || "";
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/judges/status`, { params: { event_id: eventId } });
        setJudgeStatuses(res.data);
      } catch {}
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 5000);
    return () => clearInterval(iv);
  }, [eventId]);

  const moveSection = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= upcomingSections.length) return;
    const newOrder = [...upcomingSections];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    reorderSections(newOrder.map(s => s.tournament_id));
  };

  const handleDragStart = (idx) => { setDragIdx(idx); };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (idx) => {
    if (dragIdx !== null && dragIdx !== idx) moveSection(dragIdx, idx);
    setDragIdx(null);
  };

  return (
    <>
      {/* Judges Communications Panel — ALWAYS visible at the top, even when no battle/circle is running.
       * Lets the manager ping the judges (broadcast or individual) and see/resolve active HELP states. */}
      {judges && judges.length > 0 && commsSendMessage && (
        <Card className="p-4 glass mb-4" data-testid="judges-comms-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              {t('control.judgesPanelTitle')}
              <span className="text-xs font-normal text-muted-foreground">({judges.length})</span>
            </h3>
            <ManagerMessageButton onSend={commsSendMessage} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {[...judges].sort((a, b) => (a.judge_number || 0) - (b.judge_number || 0)).map((j) => {
              const judgeNum = j.judge_number;
              const status = judgeStatuses[judgeNum] || "inactive";
              const helpActive = commsHelpStates && commsHelpStates[judgeNum];
              return (
                <div
                  key={judgeNum}
                  className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted border border-border"
                  data-testid={`judges-panel-row-${judgeNum}`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <JudgeStatusDot status={status} />
                    <span className="text-sm font-semibold text-foreground truncate">
                      #{judgeNum} · {j.first_name || '—'}
                    </span>
                    {helpActive && (
                      <button
                        onClick={() => commsResolveHelp && commsResolveHelp(judgeNum).then(() => toast.success(t('comms.helpResolved')))}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse hover:bg-red-600 shrink-0"
                        title={t('comms.clickToResolve')}
                        data-testid={`judges-panel-help-${judgeNum}`}
                      >
                        <LifeBuoy size={12} /> HELP
                      </button>
                    )}
                  </div>
                  <ManagerMessageButton
                    judgeNumber={judgeNum}
                    judgeLabel={j.first_name}
                    onSend={commsSendMessage}
                  />
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <DryRunPanel
        simulationMode={simulationMode}
        eventId={eventId}
        currentBattle={currentBattle}
        currentCircle={currentCircle}
        upcomingSections={upcomingSections}
        onAfterRun={() => {
          loadCurrentBattle && loadCurrentBattle();
          loadUpcomingEvents && loadUpcomingEvents();
        }}
      />
      {/* Battle Control Panel */}
      {currentBattle && (
        <div className="space-y-6">
          <Card className="p-6 glass">
            <h2 className="text-3xl font-bold mb-6 text-center">{t('control.battleInProgress')}</h2>
            {countdownMode && currentBattle.battle?.id && (
              <div className="mb-6">
                <BattleTimerPanel
                  battle={currentBattle.battle}
                  athlete1={currentBattle.athlete1}
                  athlete2={currentBattle.athlete2}
                  timer={battleTimers?.[currentBattle.battle.id]}
                  onUpdate={() => refreshBattleTimer && refreshBattleTimer(currentBattle.battle.id)}
                />
              </div>
            )}
            <div className="flex justify-around items-center mb-6">
              <div className="text-center">
                {currentBattle.athlete1?.photo_url && <img src={currentBattle.athlete1.photo_url} alt="" className="w-24 h-24 rounded-full mx-auto mb-3" />}
                <h3 className="text-xl font-bold">{currentBattle.athlete1?.name}</h3>
                <p className="text-muted-foreground">{currentBattle.athlete1?.country}</p>
              </div>
              <div className="text-4xl font-bold text-purple-500">VS</div>
              <div className="text-center">
                {currentBattle.athlete2?.photo_url && <img src={currentBattle.athlete2.photo_url} alt="" className="w-24 h-24 rounded-full mx-auto mb-3" />}
                <h3 className="text-xl font-bold">{currentBattle.athlete2?.name}</h3>
                <p className="text-muted-foreground">{currentBattle.athlete2?.country}</p>
              </div>
            </div>

            <div className="mb-6">
              {(() => {
                const battleTournament = tournaments.find(t => t.id === currentBattle.battle?.tournament_id);
                const requiredJudgesCount = battleTournament?.judges_count || judgesCount;
                return (
                  <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                    <h3 className="text-xl font-bold">{t('control.judgeVotesCount').replace('{count}', currentBattle.votes?.length).replace('{total}', requiredJudgesCount)}</h3>
                    <div className="flex items-center gap-2">
                      {commsSendMessage && (
                        <ManagerMessageButton onSend={commsSendMessage} />
                      )}
                      {currentBattle.votes?.length === requiredJudgesCount && currentBattle.votes.some(v => !v.revealed) && (
                        <Button onClick={revealAllVotes} variant="outline" data-testid="reveal-all-btn">
                          <Eye size={16} className="mr-2" /> {t('control.revealAll')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div className="space-y-2">
                {(() => {
                  const battleTournament = tournaments.find(t => t.id === currentBattle.battle?.tournament_id);
                  const tournamentJudgesCount = battleTournament?.judges_count || judgesCount;
                  const assignedJudges = (battleTournament?.assigned_judge_numbers?.length > 0)
                    ? battleTournament.assigned_judge_numbers
                    : Array.from({length: tournamentJudgesCount}, (_, i) => i + 1);
                  const anyRevealed = currentBattle.votes?.some(v => v.revealed);
                  return assignedJudges.map((judgeNum) => {
                    const vote = currentBattle.votes?.find(v => v.judge_number === judgeNum);
                    const voteIndex = currentBattle.votes?.findIndex(v => v.judge_number === judgeNum);
                    const judge = judges.find(j => j.judge_number === judgeNum);
                    return (
                      <div key={judgeNum} className="flex justify-between items-center p-3 bg-muted rounded-lg flex-wrap gap-2">
                        <span className="font-bold text-foreground text-xl flex items-center gap-2">
                          <JudgeStatusDot status={judgeStatuses[judgeNum] || "inactive"} />
                          {t('judge.loginTitle').replace('{num}', judgeNum)} {judge ? `- ${judge.first_name}` : ''}
                          {commsHelpStates && commsHelpStates[judgeNum] && (
                            <button
                              onClick={() => commsResolveHelp && commsResolveHelp(judgeNum).then(() => toast.success(t('comms.helpResolved')))}
                              className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse hover:bg-red-600"
                              title={t('comms.clickToResolve')}
                              data-testid={`judge-help-badge-${judgeNum}`}
                            >
                              <LifeBuoy size={12} /> HELP
                            </button>
                          )}
                          {commsSendMessage && (
                            <ManagerMessageButton
                              judgeNumber={judgeNum}
                              judgeLabel={judge?.first_name}
                              onSend={commsSendMessage}
                            />
                          )}
                        </span>
                        {vote ? (
                          <div className="flex items-center gap-4">
                            <div className="text-sm">
                              <span className="text-primary">{currentBattle.athlete1?.name}: {vote.athlete1_score}</span>
                              <span className="mx-2">|</span>
                              <span className="text-pink-500 dark:text-pink-400">{currentBattle.athlete2?.name}: {vote.athlete2_score}</span>
                            </div>
                            {vote.revealed ? (
                              <span className="text-green-400 text-sm">✓ {t('control.revealed')}</span>
                            ) : (
                              <Button onClick={() => revealVote(voteIndex)} size="sm"
                                disabled={currentBattle.votes?.length !== assignedJudges.length} data-testid={`reveal-vote-${judgeNum}`}>
                                <Eye size={16} className="mr-2" /> {t('control.revealVote')}
                              </Button>
                            )}
                            <Button onClick={async () => {
                                if (window.confirm(t('control.deleteVoteConfirm'))) {
                                  try { await axios.delete(`${API}/battle/${currentBattle.battle.id}/vote/${vote.judge_id}`); toast.success(t('control.voteDeleted')); loadCurrentBattle(); } catch { toast.error(t('common.error')); }
                                }
                              }} size="sm" variant="destructive" data-testid={`delete-vote-${judgeNum}`}>
                              <Trash2 size={16} className="mr-2" /> {t('control.deleteVote')}
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2" data-testid={`manual-vote-battle-${judgeNum}`}>
                            <span className="text-muted-foreground text-sm mr-1">{t('control.manualScore')}:</span>
                            <select value={manualBattleScores[judgeNum]?.a1 || ''}
                              onChange={e => setManualBattleScores(prev => ({...prev, [judgeNum]: {...(prev[judgeNum]||{}), a1: parseInt(e.target.value)||''}}))}
                              className="bg-muted text-foreground border border-border rounded px-2 py-1 text-sm" disabled={anyRevealed} data-testid={`manual-a1-score-${judgeNum}`}>
                              <option value="">{currentBattle.athlete1?.name?.substring(0,8)}</option>
                              {[1,2,3,4,5,6,7,8,9,10].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <select value={manualBattleScores[judgeNum]?.a2 || ''}
                              onChange={e => setManualBattleScores(prev => ({...prev, [judgeNum]: {...(prev[judgeNum]||{}), a2: parseInt(e.target.value)||''}}))}
                              className="bg-muted text-foreground border border-border rounded px-2 py-1 text-sm" disabled={anyRevealed} data-testid={`manual-a2-score-${judgeNum}`}>
                              <option value="">{currentBattle.athlete2?.name?.substring(0,8)}</option>
                              {[1,2,3,4,5,6,7,8,9,10].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <Button onClick={() => submitManagerBattleVote(judgeNum)} size="sm"
                              disabled={anyRevealed || !manualBattleScores[judgeNum]?.a1 || !manualBattleScores[judgeNum]?.a2} data-testid={`submit-manual-vote-${judgeNum}`}>
                              {t('control.submitManualVote')}
                            </Button>
                            {anyRevealed && <span className="text-orange-400 text-xs">{t('control.lockedAfterReveal')}</span>}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Total scores — visible dès qu'il y a des votes (pas besoin d'attendre le reveal) */}
            {currentBattle.votes?.length > 0 && (
              <div className="space-y-4">
                <div className="p-4 bg-primary/10 rounded-lg">
                  <h4 className="font-bold mb-2">{t('control.totalScores')}</h4>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-muted-foreground">{currentBattle.athlete1?.name}</p>
                      <p className="text-3xl font-bold text-primary">{currentBattle.votes.reduce((sum, v) => sum + v.athlete1_score, 0)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{currentBattle.athlete2?.name}</p>
                      <p className="text-3xl font-bold text-pink-500 dark:text-pink-400">{currentBattle.votes.reduce((sum, v) => sum + v.athlete2_score, 0)}</p>
                    </div>
                  </div>
                </div>
                {(() => {
                  const bt = tournaments.find(t => t.id === currentBattle.battle?.tournament_id);
                  const req = bt?.judges_count || judgesCount;
                  if (currentBattle.votes?.length >= req) {
                    const allRevealed = currentBattle.votes.every(v => v.revealed);
                    return (
                      <>
                        <Button onClick={finishBattle} className="w-full" size="lg"
                          disabled={!allRevealed}
                          title={!allRevealed ? t('control.revealBeforeFinish') : ''}
                          data-testid="finish-battle-btn">
                          <Trophy className="mr-2" /> {t('control.finishBattle')}
                        </Button>
                        <Button onClick={finishBattleAndNext} className="w-full mt-2 bg-green-600 hover:bg-green-700" size="lg"
                          disabled={!allRevealed || !getNextBattle()}
                          title={!allRevealed ? t('control.revealBeforeFinish') : ''}
                          data-testid="finish-and-next-battle-btn">
                          <SkipForward className="mr-2" /> {t('control.finishAndNextBattle')}
                        </Button>
                        {!allRevealed && (
                          <p className="text-center text-sm text-orange-600 dark:text-orange-400 mt-2">
                            {t('control.revealBeforeFinish')}
                          </p>
                        )}
                      </>
                    );
                  }
                  return (
                    <div className="p-4 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg text-center">
                      <p className="text-yellow-600 dark:text-yellow-400">{t('control.waitingAllVotes').replace('{count}', currentBattle.votes?.length).replace('{total}', req)}</p>
                    </div>
                  );
                })()}
              </div>
            )}
            {(!currentBattle.votes || currentBattle.votes.length === 0) && (
              <div className="p-4 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg text-center">
                <p className="text-yellow-600 dark:text-yellow-400">{t('control.waitingAllVotes').replace('{count}', 0).replace('{total}', (tournaments.find(t => t.id === currentBattle.battle?.tournament_id)?.judges_count || judgesCount))}</p>
              </div>
            )}
          </Card>

          <div className="flex gap-3 flex-wrap">
            <Button onClick={() => resetBattle(currentBattle.battle.id)} variant="outline"
              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20" data-testid="control-reset-battle-btn">
              <RotateCcw size={16} className="mr-2" /> Reset
            </Button>
            <Button onClick={() => { setForfeitBattleId(currentBattle.battle.id); setForfeitModalOpen(true); }}
              variant="outline" className="border-yellow-500/50 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/20" data-testid="control-forfeit-battle-btn">
              <UserX size={16} className="mr-2" /> {t('battle.forfeit')}
            </Button>
          </div>
        </div>
      )}

      {/* Circle Control Panel */}
      {currentCircle && (
        <div className="space-y-6 mt-6">
          <Card className="p-6 glass">
            <h2 className="text-3xl font-bold mb-6 text-center">{t('control.circleInProgress').replace('{num}', currentCircle.circle.circle_number)}</h2>
            {countdownMode && currentCircle.circle?.id && (
              <div className="mb-6">
                <CircleTimerPanel
                  circle={currentCircle.circle}
                  athletes={currentCircle.athletes || []}
                  timer={circleTimers?.[currentCircle.circle.id]}
                  onUpdate={() => refreshCircleTimer && refreshCircleTimer(currentCircle.circle.id)}
                />
              </div>
            )}
            <div className={`grid ${currentCircle.athletes.filter(a=>a).length === 4 ? 'grid-cols-4' : 'grid-cols-3'} gap-4 mb-6`}>
              {currentCircle.athletes.map((athlete) => {
                if (!athlete) return null;
                const isForfeited = (currentCircle.circle.forfeited_ids || []).includes(athlete.id);
                return (
                  <div key={athlete.id} className="text-center" style={isForfeited ? { opacity: 0.4 } : {}}>
                    {athlete.photo_url && <img src={athlete.photo_url} alt="" className="w-20 h-20 rounded-full mx-auto mb-2" />}
                    <h3 className={`text-lg font-bold ${isForfeited ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{athlete.name}</h3>
                    <p className="text-sm text-muted-foreground">{athlete.country}</p>
                    {isForfeited && <p className="text-sm text-red-400 font-bold">{t('battle.forfeit')}</p>}
                    {!isForfeited && currentCircle.circle.status === 'voting' && (
                      <Button onClick={() => circleForfeit(currentCircle.circle.id, athlete.id)} size="sm" variant="ghost" className="text-red-400 text-xs mt-1">
                        <UserX size={12} className="mr-1" /> {t('battle.forfeit')}
                      </Button>
                    )}
                    {isForfeited && currentCircle.circle.status !== 'completed' && (
                      <Button onClick={() => circleCancelForfeit(currentCircle.circle.id, athlete.id)} size="sm" variant="ghost" className="text-green-400 text-xs mt-1">
                        <RotateCcw size={12} className="mr-1" /> {t('circle.cancelForfeit')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mb-6">
              {(() => {
                const tournamentInfo = currentCircle.tournament;
                const requiredJudgesCount = tournamentInfo?.judges_count || 5;
                const assignedJudgeNumbers = tournamentInfo?.assigned_judge_numbers || [];
                const effectiveJudgeNumbers = assignedJudgeNumbers.length > 0 ? assignedJudgeNumbers : Array.from({length: requiredJudgesCount}, (_, i) => i + 1);
                const votes = currentCircle.votes || [];
                const forfeited = currentCircle.circle.forfeited_ids || [];
                const activeAthletes = currentCircle.athletes.filter(a => a && !forfeited.includes(a.id));
                return (
                  <>
                    <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                      <h3 className="text-xl font-bold">{t('control.judgeVotesCount').replace('{count}', votes.length).replace('{total}', requiredJudgesCount)}</h3>
                      <div className="flex items-center gap-2">
                        {commsSendMessage && (
                          <ManagerMessageButton onSend={commsSendMessage} />
                        )}
                        {votes.length >= requiredJudgesCount && votes.some(v => !v.revealed) && (
                          <Button onClick={() => circleRevealAll(currentCircle.circle.id)} variant="outline" data-testid="circle-reveal-all-btn">
                            <Eye size={16} className="mr-2" /> {t('control.revealAll')}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {effectiveJudgeNumbers.map(judgeNum => {
                        const vote = votes.find(v => v.judge_number === judgeNum);
                        const voteIndex = votes.findIndex(v => v.judge_number === judgeNum);
                        const judge = judges.find(j => j.judge_number === judgeNum);
                        const anyCircleRevealed = votes.some(v => v.revealed);
                        return (
                          <div key={judgeNum} className="flex justify-between items-center p-3 bg-muted rounded-lg flex-wrap gap-2">
                            <span className="font-bold text-foreground text-xl flex items-center gap-2">
                              <JudgeStatusDot status={judgeStatuses[judgeNum] || "inactive"} />
                              {t('judge.loginTitle').replace('{num}', judgeNum)} {judge ? `- ${judge.first_name}` : ''}
                              {commsHelpStates && commsHelpStates[judgeNum] && (
                                <button
                                  onClick={() => commsResolveHelp && commsResolveHelp(judgeNum).then(() => toast.success(t('comms.helpResolved')))}
                                  className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse hover:bg-red-600"
                                  title={t('comms.clickToResolve')}
                                  data-testid={`circle-judge-help-badge-${judgeNum}`}
                                >
                                  <LifeBuoy size={12} /> HELP
                                </button>
                              )}
                              {commsSendMessage && (
                                <ManagerMessageButton
                                  judgeNumber={judgeNum}
                                  judgeLabel={judge?.first_name}
                                  onSend={commsSendMessage}
                                />
                              )}
                            </span>
                            {vote ? (
                              <div className="flex items-center gap-4">
                                <div className="text-sm flex gap-2">
                                  {activeAthletes.map(a => (<span key={a.id} className="text-primary">{a.name}: {vote.scores?.[a.id] || '-'}</span>))}
                                </div>
                                {vote.revealed ? (<span className="text-green-400 text-sm">✓ {t('control.revealed')}</span>) : (
                                  <Button onClick={() => circleRevealVote(currentCircle.circle.id, voteIndex)} size="sm"
                                    disabled={votes.length < effectiveJudgeNumbers.length} data-testid={`circle-reveal-vote-${judgeNum}`}>
                                    <Eye size={16} className="mr-2" /> {t('control.revealVote')}
                                  </Button>
                                )}
                                <Button onClick={() => circleDeleteVote(currentCircle.circle.id, vote.judge_id)} size="sm" variant="destructive" data-testid={`circle-delete-vote-${judgeNum}`}>
                                  <Trash2 size={16} className="mr-2" /> {t('control.deleteVote')}
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-wrap" data-testid={`manual-vote-circle-${judgeNum}`}>
                                <span className="text-muted-foreground text-sm mr-1">{t('control.manualScore')}:</span>
                                {activeAthletes.map(a => (
                                  <select key={a.id} value={manualCircleScores[judgeNum]?.[a.id] || ''}
                                    onChange={e => setManualCircleScores(prev => ({...prev, [judgeNum]: {...(prev[judgeNum]||{}), [a.id]: parseInt(e.target.value)||''}}))}
                                    className="bg-muted text-foreground border border-border rounded px-2 py-1 text-sm" disabled={anyCircleRevealed} data-testid={`manual-circle-score-${judgeNum}-${a.id}`}>
                                    <option value="">{a.name?.substring(0,8)}</option>
                                    {[1,2,3,4,5,6,7,8,9,10].map(s => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                ))}
                                <Button onClick={() => submitManagerCircleVote(judgeNum)} size="sm"
                                  disabled={anyCircleRevealed || !manualCircleScores[judgeNum] || Object.keys(manualCircleScores[judgeNum]||{}).length < activeAthletes.length}
                                  data-testid={`submit-manual-circle-vote-${judgeNum}`}>{t('control.submitManualVote')}</Button>
                                {anyCircleRevealed && <span className="text-orange-400 text-xs">{t('control.lockedAfterReveal')}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Total scores — visible dès qu'il y a des votes */}
                    {votes.length > 0 && (
                      <div className="space-y-4 mt-4">
                        <div className="p-4 bg-primary/10 rounded-lg">
                          <h4 className="font-bold mb-2">{t('control.totalScores')}</h4>
                          <div className={`grid grid-cols-${activeAthletes.length} gap-4 text-center`}>
                            {activeAthletes.map(a => {
                              const total = votes.reduce((sum, v) => sum + (v.scores?.[a.id] || 0), 0);
                              return (<div key={a.id}><p className="text-muted-foreground">{a.name}</p><p className="text-3xl font-bold text-primary">{total}</p></div>);
                            })}
                            {forfeited.map(fid => {
                              const a = currentCircle.athletes.find(at => at?.id === fid);
                              return a ? (<div key={fid} style={{ opacity: 0.4 }}><p className="text-muted-foreground line-through">{a.name}</p><p className="text-xl font-bold text-red-400">{t('battle.forfeit')}</p></div>) : null;
                            })}
                          </div>
                        </div>
                        {votes.length >= requiredJudgesCount && (() => {
                          const allRevealed = votes.every(v => v.revealed);
                          return (
                            <div className="space-y-2">
                              <Button onClick={() => circleFinish(currentCircle.circle.id)} className="w-full" size="lg"
                                disabled={!allRevealed}
                                title={!allRevealed ? t('control.revealBeforeFinish') : ''}
                                data-testid="circle-finish-btn">
                                <Trophy className="mr-2" /> {t('control.finishCircle')}
                              </Button>
                              <Button onClick={() => finishCircleAndNext(currentCircle.circle.id)} className="w-full bg-green-600 hover:bg-green-700" size="lg"
                                disabled={!allRevealed}
                                title={!allRevealed ? t('control.revealBeforeFinish') : ''}
                                data-testid="finish-and-next-circle-btn">
                                <SkipForward className="mr-2" /> {t('control.finishAndNextCircle')}
                              </Button>
                              {!allRevealed && (
                                <p className="text-center text-sm text-orange-600 dark:text-orange-400">
                                  {t('control.revealBeforeFinish')}
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {votes.length < requiredJudgesCount && votes.length > 0 && (
                      <div className="p-4 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg text-center mt-4">
                        <p className="text-yellow-600 dark:text-yellow-400">{t('control.waitingAllVotes').replace('{count}', votes.length).replace('{total}', requiredJudgesCount)}</p>
                      </div>
                    )}
                    {votes.length === 0 && (
                      <div className="p-4 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg text-center mt-4">
                        <p className="text-yellow-600 dark:text-yellow-400">{t('control.waitingAllVotes').replace('{count}', 0).replace('{total}', requiredJudgesCount)}</p>
                      </div>
                    )}
                    <div className="mt-4">
                      <Button onClick={() => circleReset(currentCircle.circle.id)} variant="outline"
                        className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20" data-testid="circle-reset-btn">
                        <RotateCcw size={16} className="mr-2" /> {t('control.resetCircle')}
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          </Card>
        </div>
      )}

      {/* Upcoming Events — ALWAYS visible, grouped by tournament section */}
      {upcomingSections.length > 0 && (
        <Card className="p-6 glass mt-6" data-testid="upcoming-events-section">
          <h3 className="text-2xl font-bold mb-4 text-foreground">{t('control.upcomingEvents')}</h3>
          <div className="space-y-4">
            {upcomingSections.map((section, sIdx) => (
              <div key={section.tournament_id}
                draggable
                onDragStart={() => handleDragStart(sIdx)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(sIdx)}
                className={`rounded-lg border ${dragIdx === sIdx ? 'border-purple-500 bg-primary/10' : 'border-border bg-muted'} transition-all`}
                data-testid={`upcoming-section-${section.tournament_id}`}
              >
                {/* Section header with drag handle + arrows */}
                <div className="flex items-center justify-between p-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <GripVertical size={18} className="text-muted-foreground cursor-grab" />
                    <span className="font-bold text-foreground text-lg">{section.tournament_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-purple-300">
                      {section.tournament_type === 'circle_qualification' ? 'Circles' : 'Battles'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({section.circles.length + section.battles.length})
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button onClick={() => moveSection(sIdx, sIdx - 1)} variant="ghost" size="sm" disabled={sIdx === 0}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" data-testid={`section-up-${section.tournament_id}`}>
                      <ChevronUp size={16} />
                    </Button>
                    <Button onClick={() => moveSection(sIdx, sIdx + 1)} variant="ghost" size="sm" disabled={sIdx === upcomingSections.length - 1}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" data-testid={`section-down-${section.tournament_id}`}>
                      <ChevronDown size={16} />
                    </Button>
                  </div>
                </div>
                {/* Items in this section */}
                <div className="p-2 space-y-1">
                  {section.circles.map(c => {
                    const circleAthletes = c.athlete_ids?.map(aid => athletes.find(a => a?.id === aid)).filter(Boolean) || [];
                    return (
                      <div key={c.id} className="flex items-center justify-between p-2 bg-muted rounded" data-testid={`upcoming-circle-${c.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-primary font-bold text-sm shrink-0">C{c.circle_number}</span>
                          <span className="text-foreground text-sm truncate">{circleAthletes.map(a => a.name).join(', ') || 'TBD'}</span>
                        </div>
                        <Button onClick={() => startCircle && startCircle(c.id)} size="sm" className="bg-green-600 hover:bg-green-700 shrink-0 ml-2"
                          disabled={c.athlete_ids?.some(id => !id)} data-testid={`upcoming-start-circle-${c.id}`}>
                          <Play size={14} className="mr-1" /> Start
                        </Button>
                      </div>
                    );
                  })}
                  {section.battles.map(b => {
                    const a1 = athletes.find(a => a?.id === b.athlete1_id);
                    const a2 = athletes.find(a => a?.id === b.athlete2_id);
                    return (
                      <div key={b.id} className="flex items-center justify-between p-2 bg-muted rounded" data-testid={`upcoming-battle-${b.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-primary font-bold text-sm shrink-0">B{b.match_number}</span>
                          <span className="text-foreground text-sm truncate">{a1?.name || 'TBD'} vs {a2?.name || 'TBD'}</span>
                        </div>
                        <Button onClick={() => startBattle(b.id)} size="sm" className="bg-green-600 hover:bg-green-700 shrink-0 ml-2" data-testid={`upcoming-start-battle-${b.id}`}>
                          <Play size={14} className="mr-1" /> Start
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!currentBattle && !currentCircle && upcomingSections.length === 0 && (
        <Card className="p-12 glass text-center">
          <p className="text-xl text-muted-foreground">{t('control.noBattle')}</p>
        </Card>
      )}
    </>
  );
};
